/**
 * LocationPuck — Uber/Lyft-style live user puck for Google Maps.
 *
 * Rendering uses an AdvancedMarkerElement with a DOM puck node so:
 *   - The cone rotates via `transform: rotate(θ)` (GPU-accelerated, 60fps).
 *   - The blue dot stays upright at the rotation center.
 *   - The cone fades in/out based on heading-confidence.
 *
 * Movement uses a 1-step RAF lerp toward each new (lat, lng) — between
 * updates the marker visually glides toward the latest Kalman-smoothed
 * fix. The position is fed via the parent (typically `useUserPuck`).
 *
 * Camera modes:
 *   "static"     — never touch the camera. Useful for static dashboards.
 *   "follow"     — pan camera to keep the puck centered. Map stays north-up.
 *   "navigation" — pan AND rotate camera so the puck always points "up".
 *                  Adds the configured tilt for that rideshare 3D feel.
 *
 * Designed so the React tree only owns ONE puck. Mount it inside any
 * component that already holds a `map` ref.
 */
import { useEffect, useRef } from "react";

const PUCK_SVG = `
<svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="cone-grad" cx="50%" cy="100%" r="80%">
      <stop offset="0%" stop-color="#3D8BFD" stop-opacity="0.55"/>
      <stop offset="55%" stop-color="#3D8BFD" stop-opacity="0.22"/>
      <stop offset="100%" stop-color="#3D8BFD" stop-opacity="0"/>
    </radialGradient>
    <filter id="dot-shadow" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#0A1128" flood-opacity="0.28"/>
    </filter>
  </defs>
  <!-- Directional cone (60° fan). The y=60 anchor is the dot center. -->
  <g class="lp-cone" style="transform-origin:60px 60px;">
    <path d="M60 60 L8 6 Q60 -8 112 6 Z" fill="url(#cone-grad)"/>
  </g>
  <!-- Solid blue dot with white halo (always upright, no rotation). -->
  <circle cx="60" cy="60" r="13" fill="#FFFFFF" filter="url(#dot-shadow)"/>
  <circle cx="60" cy="60" r="10" fill="#1664E0"/>
</svg>`.trim();

export default function LocationPuck({
  map,                 // google.maps.Map | null
  lat, lng,            // smoothed coords from useUserPuck
  heading = 0,         // smoothed heading (deg, north=0)
  headingConfident = false,
  mode = "static",     // "static" | "follow" | "navigation"
  tiltDeg = 50,        // tilt applied in navigation mode
  followZoom = 17,
  onUserGesture,       // called when the user manually pans/zooms while in follow/nav mode — parent should drop back to "static"
}) {
  const markerRef = useRef(null);
  const elRef = useRef(null);
  const coneRef = useRef(null);
  const animRef = useRef(null);
  const lastPosRef = useRef(null);
  const lastHeadingRef = useRef(0);
  // Pause auto-follow when the driver pans/zooms manually. We resume
  // 4s after their last gesture so the camera isn't fighting them.
  const userInteractingUntilRef = useRef(0);
  const isUserActive = () => Date.now() < userInteractingUntilRef.current;

  // Listen for user gestures on the map so we can pause auto-pan/rotate.
  // The previous behavior was to pause for 4s then auto-resume — which
  // is what the user reported as the map "snapping back" after they
  // panned or zoomed. We now fire `onUserGesture` so the parent can
  // drop OUT of follow/navigation mode entirely. The driver / rider
  // re-engages follow by tapping the locate-me button again (standard
  // Apple Maps / Google Maps behavior).
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const onUserGestureRef = useRef(onUserGesture);
  onUserGestureRef.current = onUserGesture;
  useEffect(() => {
    if (!map) return undefined;
    const armAndDropFollow = () => {
      userInteractingUntilRef.current = Date.now() + 4000;
      // If we were actively following, tell the parent to back off —
      // the user is now driving the camera and we shouldn't fight them.
      if (modeRef.current === "follow" || modeRef.current === "navigation") {
        onUserGestureRef.current?.();
      }
    };
    const div = map.getDiv?.();
    if (div) {
      div.addEventListener("pointerdown", armAndDropFollow, { passive: true });
      div.addEventListener("touchstart", armAndDropFollow, { passive: true });
      div.addEventListener("wheel", armAndDropFollow, { passive: true });
    }
    const dragstart = map.addListener("dragstart", armAndDropFollow);
    const zoomChanged = map.addListener("zoom_changed", armAndDropFollow);
    return () => {
      if (div) {
        div.removeEventListener("pointerdown", armAndDropFollow);
        div.removeEventListener("touchstart", armAndDropFollow);
        div.removeEventListener("wheel", armAndDropFollow);
      }
      window.google?.maps?.event?.removeListener(dragstart);
      window.google?.maps?.event?.removeListener(zoomChanged);
    };
  }, [map]);

  // 1. Mount the AdvancedMarkerElement once Google Maps is ready.
  useEffect(() => {
    if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) return undefined;
    // Wrap the 120×120 SVG inside a zero-sized anchor div positioned so
    // that the SVG's geometric center (60, 60 — where the dot sits)
    // coincides EXACTLY with the marker's lat/lng anchor. Without this
    // shim, AdvancedMarkerElement anchors at the content's bottom-center
    // (60, 120 in our SVG), which puts the visible dot 60 screen-pixels
    // ABOVE the true coordinate — and that offset is constant across
    // zooms, so the dot appears to drift as you zoom in and out.
    const wrap = document.createElement("div");
    wrap.style.position = "relative";
    wrap.style.width = "0px";
    wrap.style.height = "0px";
    wrap.style.pointerEvents = "none";
    wrap.innerHTML = PUCK_SVG;
    const svg = wrap.firstElementChild;
    svg.style.position = "absolute";
    svg.style.left = "-60px";   // center horizontally on the anchor
    svg.style.top = "-60px";    // shift up so SVG center is on the anchor
    svg.style.width = "120px";
    svg.style.height = "120px";
    svg.style.transformOrigin = "60px 60px";
    svg.style.willChange = "transform";
    svg.style.pointerEvents = "none";
    const cone = svg.querySelector(".lp-cone");
    cone.style.transition = "opacity 200ms ease, transform 120ms linear";
    cone.style.opacity = headingConfident ? "1" : "0";
    elRef.current = svg;
    coneRef.current = cone;

    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      map,
      position: lat != null ? { lat, lng } : null,
      content: wrap,
      // Sit BELOW sector count markers (zIndex 100) so the user puck
      // never obscures the tappable sector circles. The puck is just
      // a position indicator — sectors are the primary tap targets.
      zIndex: 1,
    });
    markerRef.current = marker;
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      animRef.current = null;
      marker.map = null;
      markerRef.current = null;
    };
  }, [map]);

  // 2. Animate marker between fixes (lerp, RAF, ~250ms ease-out).
  useEffect(() => {
    if (!markerRef.current || lat == null || lng == null) return;
    const start = lastPosRef.current || { lat, lng };
    const end = { lat, lng };
    // Skip the lerp entirely when the new fix is sub-meter from the
    // previous endpoint — that's pure GPS noise post-Kalman and there's
    // no perceptible motion to animate. This drops the dominant React+
    // RAF storm while the user is stationary at a stoplight.
    const dLat = end.lat - start.lat;
    const dLng = end.lng - start.lng;
    const approxMeters = Math.sqrt(dLat * dLat + dLng * dLng) * 111_000;
    if (approxMeters < 0.6) {
      if (markerRef.current) markerRef.current.position = end;
      lastPosRef.current = end;
      return;
    }
    const t0 = performance.now();
    const dur = 700; // gentle catch-up between Kalman updates
    if (animRef.current) cancelAnimationFrame(animRef.current);

    const step = (now) => {
      const t = Math.min(1, (now - t0) / dur);
      // easeOutCubic
      const e = 1 - Math.pow(1 - t, 3);
      const cur = {
        lat: start.lat + (end.lat - start.lat) * e,
        lng: start.lng + (end.lng - start.lng) * e,
      };
      if (markerRef.current) markerRef.current.position = cur;
      // Auto-pan ONLY when the user isn't actively panning/zooming. The
      // 4-second "user interaction" window is the simplest correct fix
      // for the Uber-app fighting-the-finger problem.
      if ((mode === "follow" || mode === "navigation") && map && !isUserActive()) {
        map.panTo(cur);
      }
      if (t < 1) animRef.current = requestAnimationFrame(step);
      else lastPosRef.current = end;
    };
    animRef.current = requestAnimationFrame(step);
  }, [lat, lng, mode, map]);

  // 3. Apply rotation. In nav mode we rotate the MAP (heading) and keep the
  // puck pointed up; otherwise we rotate just the puck cone.
  useEffect(() => {
    const svg = elRef.current;
    const cone = coneRef.current;
    if (!svg || !cone) return;
    cone.style.opacity = headingConfident ? "1" : "0";
    if (mode === "navigation" && map) {
      // Map rotates with heading → puck stays visually up. We skip the
      // setHeading/setTilt calls while the user is gesture-interacting
      // so the map isn't yanked out of their pan. Also skip when the
      // heading change is sub-perceptual (< 1.5°) to avoid hammering the
      // GL camera on every 15Hz compass tick.
      if (!isUserActive()) {
        const targetH = heading || 0;
        const curMapH = (map.getHeading?.() || 0);
        let dh = Math.abs(targetH - curMapH);
        if (dh > 180) dh = 360 - dh;
        if (dh > 1.5) { try { map.setHeading?.(targetH); } catch { /* ignore */ } }
        if (Math.abs((map.getTilt?.() || 0) - tiltDeg) > 1) {
          try { map.setTilt?.(tiltDeg); } catch { /* ignore */ }
        }
      }
      svg.style.transform = "rotate(0deg)";
    } else {
      // Map north-up → rotate the puck SVG to show direction.
      const target = heading || 0;
      // Shortest-arc rotation to avoid the 359°→0° flip.
      let cur = lastHeadingRef.current;
      let delta = ((target - cur + 540) % 360) - 180;
      cur += delta;
      lastHeadingRef.current = cur;
      svg.style.transform = `rotate(${cur}deg)`;
      if (mode !== "navigation" && map) {
        try { map.setHeading?.(0); map.setTilt?.(0); } catch { /* ignore */ }
      }
    }
  }, [heading, headingConfident, mode, map, tiltDeg]);

  // 4. Mode-change side-effects.
  useEffect(() => {
    if (!map) return;
    if (mode === "follow" || mode === "navigation") {
      if (lat != null && lng != null) {
        map.panTo({ lat, lng });
        if (typeof map.getZoom === "function" && map.getZoom() < followZoom) {
          map.setZoom(followZoom);
        }
      }
    }
    if (mode === "static") {
      try { map.setHeading?.(0); map.setTilt?.(0); } catch { /* ignore */ }
    }
  }, [mode, map, lat, lng, followZoom]);

  return null; // headless component — pure side-effects on the map
}
